import CountUp from "react-countup";
import styles from "./numbers-display-block.module.less";
import { TTheme } from "../../../types/theme-types";
import { m as motion } from "framer-motion";
import { useAnimateFadeIn } from "../../../animation/animation";
import {
  getNumberOfDecimals,
  hasNumbers,
  isDecimalNumber,
  isNumber,
  isWholeNumber,
} from "../../../helpers/number-helper";

export type TNumberDisplayItem = {
  eyebrow?: string;
  number: string;
  label: string;
};

export type TNumbersDisplayBlockProps = {
  displayItems: Array<TNumberDisplayItem>;
  theme?: TTheme;
};

const getDecimalCharacterTypeFromString = (string: string) => {
  if (string?.includes(".")) {
    return ".";
  }

  if (string?.includes(",")) {
    return ",";
  }

  return null;
};

const addNumberAnimationsToNumber = (numberAndUnitsAsString: string) => {
  const words = numberAndUnitsAsString.split(" ");
  const onlyNumbers = words.every((word) => isWholeNumber(word));

  if (onlyNumbers) {
    const numberAsString = words.join("");
    const wordAsNumber = Number(numberAsString);

    return <CountUp key={wordAsNumber} end={wordAsNumber} separator=" " enableScrollSpy={true} />;
  }

  const toReturn = words.map((word) => {
    if (!hasNumbers(word)) {
      return (
        <span key={word} className={styles.wordWithoutNumbers}>
          {word}
        </span>
      );
    }

    if (isWholeNumber(word)) {
      const wordAsNumber = Number(word);

      return <CountUp key={wordAsNumber} end={wordAsNumber} separator=" " enableScrollSpy={true} />;
    }

    if (isDecimalNumber(word)) {
      const decimalNumberWithDotNotation = word.replace(",", ".");
      const parsedNumber = parseFloat(decimalNumberWithDotNotation);

      const numberOfDecimals = getNumberOfDecimals(parsedNumber);
      const decimalCharacterType = getDecimalCharacterTypeFromString(word);

      return (
        <CountUp
          key={parsedNumber}
          end={parsedNumber}
          separator=" "
          enableScrollSpy={true}
          decimal={decimalCharacterType ?? undefined}
          decimals={numberOfDecimals === -1 ? undefined : numberOfDecimals}
        />
      );
    }

    const stringAsIndividualChars = word.split("");

    // for example 2/3 or 23%
    const animatedIndividualLetters = stringAsIndividualChars.map((letter) => {
      const letterAsNumber = Number(letter);
      if (isNumber(letterAsNumber)) {
        return <CountUp key={letterAsNumber} end={letterAsNumber} separator=" " enableScrollSpy={true} />;
      }

      return <span key={letter}>{letter}</span>;
    });

    return animatedIndividualLetters;
  });

  return toReturn;
};

const NumberDisplayItem = ({ eyebrow, number, label }: TNumberDisplayItem) => {
  const animatedNumberTextWords = addNumberAnimationsToNumber(number);

  return (
    <li className={styles.item}>
      {eyebrow && <p className={[styles.eyebrow, "eyebrow"].join(" ")}>{eyebrow}</p>}
      <p className={["h-display", styles.number].join(" ")}>{animatedNumberTextWords}</p>
      <p className={[styles.label, "body-s"].join(" ")}>{label}</p>
    </li>
  );
};

const NumbersDisplayBlock = ({ displayItems, theme }: TNumbersDisplayBlockProps) => {
  const fadeInAnimation = useAnimateFadeIn({ whileInView: true, easing: "easeInOut" });

  return (
    <section className={[styles.wrapper, theme ? `theme-${theme}` : ""].join(" ")}>
      <motion.ul {...fadeInAnimation} className={[styles.list, "max-width padded-sides"].join(" ")}>
        {displayItems.map((item) => {
          return <NumberDisplayItem key={`${item.label}-${item.number}`} {...item} />;
        })}
      </motion.ul>
    </section>
  );
};

export default NumbersDisplayBlock;
